"use client";

import { Button } from "@pex/ui-web";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

const METHODOLOGY_STEPS = [
  {
    step: "Learn",
    description:
      "Build essential knowledge. Gain empowering insights into a multitude of key recovery-related topics.",
  },
  {
    step: "Reflect",
    description:
      "Connect key concepts to personal experiences and circumstances to enhance self-awareness and understanding.",
  },
  {
    step: "Apply",
    description:
      "Transform awareness into real-life change by actioning new insights and implementing more effective strategies.",
  },
] as const;

export function MethodologySection() {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="py-20" id="methodology">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 md:grid-cols-2 md:gap-16">
          <div className="relative aspect-[4/5] overflow-hidden rounded-2xl">
            <Image
              alt="A person reviewing a Resound workbook."
              className="object-cover"
              fill
              sizes="(max-width: 768px) 100vw, 50vw"
              src={"/images/find-help/FindHelp_LowerLeft02.webp"}
            />
          </div>

          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="heading-2 font-bold">The Methodology</h2>
              <p className="body-lg text-muted-foreground">
                A comprehensive approach that addresses knowledge, personal
                insight, and practical implementation for effective supporter
                addiction recovery.
              </p>
            </div>

            <div className="divide-y">
              {METHODOLOGY_STEPS.map((s, i) => {
                const isActive = activeIndex === i;
                return (
                  <div className="py-5 first:pt-0 last:pb-0" key={s.step}>
                    <button
                      aria-expanded={isActive}
                      className="w-full cursor-pointer text-left"
                      onClick={() => setActiveIndex(i)}
                      type="button"
                    >
                      <h3
                        className={`heading-6 transition-colors duration-200 ${
                          isActive
                            ? "font-bold text-foreground"
                            : "font-semibold text-muted-foreground"
                        }`}
                      >
                        {s.step}
                      </h3>
                    </button>
                    <div
                      className="grid transition-[grid-template-rows] duration-300 ease-in-out"
                      style={{ gridTemplateRows: isActive ? "1fr" : "0fr" }}
                    >
                      <div className="overflow-hidden">
                        <p className="body-base pt-2 text-muted-foreground">
                          {s.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div>
              <Button asChild variant="secondary-hybrid">
                <Link href="/programmes/overview">Learn More</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
